import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  TextField,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PrintIcon from "@mui/icons-material/Print";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import axios from "axios";
import * as XLSX from "xlsx";
import Navbar from "../Layout/Navbar";
import { TRUNG_THU_LOAI_BANH, TRUNG_THU_LOAI_PHIEU } from "../../constants/trungThu";
import {
  parseDateTime,
  getAdminConfirmedAt,
  getCreatedAt,
  getStatusElapsedStartAt,
} from "../../utils/trungThuReceiptTiming";

const theme = createTheme({
  palette: {
    primary: {
      main: "#d32f2f",
    },
    secondary: {
      main: "#ff6b35",
    },
  },
  breakpoints: {
    values: {
      xs: 360,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

const STORE_RECEIVE_TIMEOUT_MS = 24 * 60 * 60 * 1000;

const getWaitingTimeBadgeSx = (startAt, endAt) => {
  const startDate = parseDateTime(startAt);
  const endDate = parseDateTime(endAt);

  const baseSx = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    px: { xs: 1, md: 1.25 },
    py: 0.45,
    borderRadius: "999px",
    fontWeight: 700,
    lineHeight: 1.2,
    minWidth: { xs: 72, md: 86 },
    border: "1px solid",
    whiteSpace: "nowrap",
  };

  if (!startDate || !endDate) {
    return {
      ...baseSx,
      color: "#475569",
      bgcolor: "#f1f5f9",
      borderColor: "#cbd5e1",
    };
  }

  const totalMinutes = Math.floor(
    Math.max(0, endDate.getTime() - startDate.getTime()) / (1000 * 60)
  );

  if (totalMinutes <= 30) {
    return {
      ...baseSx,
      color: "#0369a1",
      bgcolor: "#e0f2fe",
      borderColor: "#7dd3fc",
    };
  }

  if (totalMinutes <= 120) {
    return {
      ...baseSx,
      color: "#166534",
      bgcolor: "#dcfce7",
      borderColor: "#86efac",
    };
  }

  if (totalMinutes <= 360) {
    return {
      ...baseSx,
      color: "#92400e",
      bgcolor: "#fef3c7",
      borderColor: "#fcd34d",
    };
  }

  if (totalMinutes <= 720) {
    return {
      ...baseSx,
      color: "#c2410c",
      bgcolor: "#ffedd5",
      borderColor: "#fdba74",
    };
  }

  return {
    ...baseSx,
    color: "#b91c1c",
    bgcolor: "#fee2e2",
    borderColor: "#fca5a5",
  };
};

const NhapHangTT = () => {
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [phieuNhapList, setPhieuNhapList] = useState([]);
  const [stores, setStores] = useState([]); // Add stores state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [userRole, setUserRole] = useState("user");
  const [userInfo, setUserInfo] = useState({});
  const [showAll, setShowAll] = useState(false); // Chỉ bật khi người dùng nhấn "Xem tất cả"
  const [searchText, setSearchText] = useState(""); // Thêm state cho tìm kiếm
  const [sortStatus, setSortStatus] = useState(false); // New state for sorting status
  const [sortWaitingTime, setSortWaitingTime] = useState(false);
  const [activeReceiptTab, setActiveReceiptTab] = useState("all");
  const [showExportAllButton, setShowExportAllButton] = useState(false); // Add state for export all button
  // Add date range states
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [currentTime, setCurrentTime] = useState(new Date());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingReceipt, setDeletingReceipt] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!contextMenu) return undefined;

    const preventBrowserContextMenu = (event) => {
      event.preventDefault();
    };

    document.addEventListener(
      "contextmenu",
      preventBrowserContextMenu,
      true
    );

    return () => {
      document.removeEventListener(
        "contextmenu",
        preventBrowserContextMenu,
        true
      );
    };
  }, [contextMenu]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // Determine user role from localStorage
  const getUserRole = () => {
    const maPB = localStorage.getItem("MaPB");
    const isAdmin = localStorage.getItem("isAdmin");
    const userMaCH = localStorage.getItem("IDCH");
    const userIDCH = localStorage.getItem("IDCH");

    const userInfo = {
      maPB: maPB,
      isAdmin: isAdmin === "true",
      maCH: userMaCH,
      idCH: userIDCH,
    };
    console.log("User Info:", userInfo);
    setUserInfo(userInfo);

    // Admin permissions
    if (userInfo.isAdmin && ["4", "321", "145", "170", "187", "250" , "16", "11"].includes(maPB)) {
      return "admin";
    }

    // Ban điều phối permissions
    if (["80", "53", "4", "321", "16"].includes(maPB)) {
      return "coordinator";
    }

    // Regular user
    return "user";
  };

  useEffect(() => {
    // Nếu localStorage rỗng thì quay về trang login
    if (!localStorage.getItem("MaPB") && !localStorage.getItem("IDCH")) {
      navigate("/logintt");
      return;
    }

    const role = getUserRole();
    setUserRole(role);
    fetchStores();
  }, []);

  // Add function to fetch stores
  const fetchStores = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/stores`);
      setStores(response.data);
    } catch (error) {
      console.error("Error fetching stores:", error);
    }
  };

  const getStoreDisplayName = (store, fallback = "") => {
    if (!store) return String(fallback ?? "");
    return String(
      store.TenCuaHang ||
        store.TenCuaHang_Uni ||
        store.Ten_CH ||
        store.TenCH ||
        store.MaBravo ||
        store.IDCH ||
        fallback ||
        ""
    ).trim();
  };

  // Add function to get store name by ID
  const getStoreName = (storeId) => {
    if (storeId === 222) {
      return "Khách hàng Doticom";
    }
    if (String(storeId).trim() === "333") {
      return "Biếu tặng";
    }
    if (String(storeId).trim() === "444") {
      return "Trả hủy";
    }
    if (String(storeId).trim() === "555") {
      return "Kho DC";
    }
    if (String(storeId).trim() === "666") {
      return "Kho Thanh Lý";
    }
    const store = stores.find((store) => String(store.IDCH) === String(storeId));
    return getStoreDisplayName(store, storeId);
  };

  // Add function to get MaBravo by store ID
  const getMaBravo = (storeId) => {
    const store = stores.find((store) => String(store.IDCH) === String(storeId));
    return String(store?.MaBravo || storeId || "");
  };

  const normalizeNote = (value) =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/đ/g, "d")
      .replace(/Đ/g, "D")
      .toLowerCase()
      .replace(/\s+/g, "");

  const isTrungThuReceipt = (phieu) => {
    const loaiPhieu = phieu?.loaiPhieu ?? phieu?.LoaiPhieu;
    return (
      loaiPhieu === null ||
      loaiPhieu === undefined ||
      String(loaiPhieu).trim() === "" ||
      Number(loaiPhieu) === TRUNG_THU_LOAI_PHIEU
    );
  };

  const getReceiptNoteType = (phieu) => {
    const note = normalizeNote(phieu?.ghiChu ?? phieu?.GhiChu);
    if (note.includes("dieuchuyen")) return "transfer";
    if (note.includes("trave")) return "return";
    return "normal";
  };

  const isTransferReceipt = (phieu) =>
    isTrungThuReceipt(phieu) && getReceiptNoteType(phieu) === "transfer";

  const isReturnReceipt = (phieu) =>
    isTrungThuReceipt(phieu) && getReceiptNoteType(phieu) === "return";

  const isTruthyFlag = (value) =>
    value === true || value === 1 || value === "1";

  const isStatusValue = (value, expected) => Number(value) === expected;

  const isRawTransferReceipt = (phieu) => isTransferReceipt(phieu);

  const formatElapsedTime = (startAt) => {
    const startDate = parseDateTime(startAt);
    if (!startDate) return "";

    const diffMs = Math.max(0, currentTime.getTime() - startDate.getTime());
    const totalMinutes = Math.min(
      Math.floor(diffMs / (1000 * 60)),
      STORE_RECEIVE_TIMEOUT_MS / (1000 * 60)
    );
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${hours} giờ ${minutes} phút`;
  };

  const getWaitingStartAt = (phieu) =>
    getStatusElapsedStartAt(phieu, {
      isTransferReceipt: isTransferReceipt(phieu),
      isReturnReceipt: isReturnReceipt(phieu),
    });

  const getWaitingMinutes = (phieu) => {
    const waitingStartAt = parseDateTime(getWaitingStartAt(phieu));
    if (!waitingStartAt) return -1;

    return Math.floor(
      Math.max(0, currentTime.getTime() - waitingStartAt.getTime()) /
        (1000 * 60)
    );
  };

  const hasStoreReceiveTimedOut = (phieu) => {
    const isApproved = isTruthyFlag(
      phieu?.isApproved ?? phieu?.Act_phieu
    );
    const hasStoreConfirmed =
      isStatusValue(phieu?.status ?? phieu?.Status, 1) ||
      [
        "Cửa hàng đã xác nhận",
        "Đã nhận, có chênh lệch",
        "Tự động nhận",
        "Hoàn tất",
      ].includes(phieu?.trangThai);

    if (
      hasStoreConfirmed ||
      isReturnReceipt(phieu) ||
      (isTransferReceipt(phieu) && !isApproved)
    ) {
      return false;
    }

    const createdAt =
      parseDateTime(phieu?.createdAt) ||
      parseDateTime(getCreatedAt(phieu));
    const adminConfirmedAt =
      parseDateTime(phieu?.adminConfirmedAt) ||
      parseDateTime(getAdminConfirmedAt(phieu));
    const waitingStartAt = isApproved
      ? adminConfirmedAt || createdAt
      : createdAt;

    return (
      waitingStartAt !== null &&
      currentTime.getTime() - waitingStartAt.getTime() >
        STORE_RECEIVE_TIMEOUT_MS
    );
  };

  const getDisplayTrangThai = (phieu) =>
    hasStoreReceiveTimedOut(phieu) ? "Tự động nhận" : phieu.trangThai;

  const renderWaitingTimeBadge = (phieu) => {
    const waitingStartAt = getWaitingStartAt(phieu);
    const waitingTimeText = formatElapsedTime(waitingStartAt);

    if (!waitingTimeText) return "";

    return (
      <Box
        component="span"
        sx={getWaitingTimeBadgeSx(waitingStartAt, currentTime)}
      >
        {waitingTimeText}
      </Box>
    );
  };

  const renderApprovedByBadge = (approvedBy) => {
    const approvedByText = String(approvedBy || "").trim();
    const hasApprovedBy = approvedByText.length > 0;

    return (
      <Box
        component="span"
        sx={{
          color: "white",
          bgcolor: hasApprovedBy ? "#2e7d32" : "#ed6c02",
          px: { xs: 1, md: 1.5 },
          py: 0.5,
          borderRadius: 1,
          display: "inline-block",
          fontSize: { xs: "0.8125rem", md: "0.875rem" },
          fontWeight: 700,
          lineHeight: 1.2,
        }}
      >
        {hasApprovedBy ? approvedByText : "Chưa duyệt"}
      </Box>
    );
  };

  const deriveTrangThai = (phieu, { hasDiscrepancy, hasSLThucNhan = true }) => {
    if (isRawTransferReceipt(phieu)) {
      if (!isTruthyFlag(phieu.Act_phieu)) {
        return "Chờ admin xác nhận";
      }

      if (isStatusValue(phieu.Status, 1)) {
        return "Hoàn tất";
      }

      return "Chờ cửa hàng nhận";
    }

    if (isTruthyFlag(phieu.Act_phieu)) {
      return "Đã duyệt";
    }

    if (!hasSLThucNhan) {
      return "Đã gửi phiếu";
    }

    if (isStatusValue(phieu.Status, 1) && hasDiscrepancy) {
      return "Đã nhận, có chênh lệch";
    }

    if (isStatusValue(phieu.Status, 1)) {
      return "Cửa hàng đã xác nhận";
    }

    return "Đã gửi phiếu";
  };

  const isExportableReceipt = (phieu) => {
    if (isTransferReceipt(phieu)) {
      return phieu.isApproved === true && isStatusValue(phieu.status, 1);
    }

    return (
      phieu.trangThai === "Đã duyệt" ||
      phieu.trangThai === "Cửa hàng đã xác nhận"
    );
  };

  const isPendingTransferReceipt = (phieu) =>
    isTransferReceipt(phieu) && phieu?.isApproved !== true;

  const isTransferOrReturnReceipt = (phieu) =>
    isTransferReceipt(phieu) || isReturnReceipt(phieu);

  const getReceiverDisplayName = (phieu) => {
    const storeName = getStoreName(phieu?.khachHang);
    const tenKH = String(phieu?.tenKH || "").trim();
    const receiverName = tenKH ? `${storeName} - ${tenKH}` : storeName;

    if (isTransferReceipt(phieu)) return `${receiverName} (Điều chuyển)`;
    if (isReturnReceipt(phieu)) return `${receiverName} (Trả về)`;
    return receiverName;
  };

  const isReceiptReceiver = (phieu, storeId) =>
    String(phieu?.khachHang) === String(storeId);

  const isReceiptSender = (phieu, storeId) =>
    isTransferOrReturnReceipt(phieu) &&
    String(phieu?.nguoiGui) === String(storeId);

  const isReceiptVisibleForStore = (phieu, storeId) =>
    isReceiptReceiver(phieu, storeId) || isReceiptSender(phieu, storeId);

  const canUserSeeReceipt = (phieu) => {
    if (
      userRole === "user" &&
      isPendingTransferReceipt(phieu) &&
      isReceiptReceiver(phieu, userInfo.idCH) &&
      !isReceiptSender(phieu, userInfo.idCH)
    ) {
      return false;
    }

    return true;
  };

  // Thêm hàm lấy tên cửa hàng theo MaCH (dùng cho filter)
  const getStoreNameByMaCH = (maCH) => {
    if (maCH === 222) return "Khách hàng Doticom";
    if (String(maCH).trim() === "333") return "Biếu tặng";
    if (String(maCH).trim() === "444") return "Trả hủy";
    if (String(maCH).trim() === "555") return "Kho DC";
    if (String(maCH).trim() === "666") return "Kho Thanh Lý";
    const store = stores.find((store) => String(store.IDCH) === String(maCH));
    return getStoreDisplayName(store, maCH);
  };

  useEffect(() => {
    // Chỉ fetch khi có userRole và userInfo
    if (userRole && (userRole !== "user" || userInfo.idCH)) {
      if (showAll) {
        fetchPhieuNhapAll(fromDate, toDate);
      } else {
        fetchPhieuNhap();
      }
    }
    // eslint-disable-next-line
  }, [selectedDate, userRole, userInfo, showAll]);

  const fetchPhieuNhap = async () => {
    try {
      const formattedDate = format(selectedDate, "yyyy-MM-dd");
      let apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/sale-nhap-hang-admin`;
      let params = { NgayNhap: formattedDate };
      // Add filtering based on user role
      if (userRole === "coordinator") {
        params.CreatedBy = userInfo.maPB; // Assuming coordinator can only see their created receipts
      }

      const response = await axios.get(apiUrl, { params });
      let data = response.data.map((phieu) => {
        // Check if there`s a discrepancy between ordered and original quantities
        const hasDiscrepancy = phieu.Items.some(
          (item) => item.SLNhapDau !== item.SoLuong
        );

        const trangThai = deriveTrangThai(phieu, { hasDiscrepancy });

        // Calculate total quantity
        let tongSoLuong;
        if (isTruthyFlag(phieu.Act_phieu)) {
          // Use SLThucNhan for approved receipts, fallback to SLNhapDau
          tongSoLuong = phieu.Items.reduce(
            (sum, item) =>
              sum + (Number(item.SLThucNhan || item.SLNhapDau) || 0),
            0
          );
        } else {
          // Use SLNhapDau for non-approved receipts
          tongSoLuong = phieu.Items.reduce(
            (sum, item) => sum + (Number(item.SLNhapDau) || 0),
            0
          );
        }

        return {
          id: phieu.Phieu,
          soPhieu: phieu.SoPN,
          ngay: new Date(phieu.NgayNhap),
          createdAt: getCreatedAt(phieu),
          adminConfirmedAt: getAdminConfirmedAt(phieu),
          khachHang: phieu.MaCH,
          tenKH: phieu.TenKH || "",
          nguoiGui: phieu.MaCH_DC, // <-- add sender store id
          trangThai: trangThai,
          tongSoLuong: tongSoLuong,
          items: phieu.Items,
          createdBy: phieu.CreatedBy,
          isApproved: isTruthyFlag(phieu.Act_phieu),
          nguoiXacNhan: phieu.NguoiXacNhan || "",
          ghiChu: phieu.GhiChu,
          status: phieu.Status,
          loaiPhieu: phieu.LoaiPhieu || null, // Add LoaiPhieu field
        };
      });

      // Filter for records containing "Nhập bánh trung thu" in GhiChu or Status = 77
      data = data.filter(
        (phieu) =>
          (phieu.ghiChu && phieu.ghiChu.includes("Nhập bánh trung thu")) ||
          isTransferReceipt(phieu) ||
          isReturnReceipt(phieu)
      );

      console.log("Raw data:", data);
      console.log("User role:", userRole);
      console.log("User IDCH:", userInfo.idCH);

      // Special handling for IDCH 555 - show transfer/return receipts.
      if (userInfo.idCH === "555" || userInfo.idCH === 555) {
        data = data.filter(
          (phieu) =>
            (isTransferReceipt(phieu) || isReturnReceipt(phieu)) &&
            canUserSeeReceipt(phieu)
        );
      } else {
        // Filter data for user role based on IDCH from localStorage
        if (userRole === "user" && userInfo.idCH) {
          const originalLength = data.length;
          data = data.filter((phieu) => {
            const match = isReceiptVisibleForStore(phieu, userInfo.idCH);
            console.log(
              `Comparing phieu.khachHang: ${
                phieu.khachHang
              } (${typeof phieu.khachHang}) with userInfo.idCH: ${
                userInfo.idCH
              } (${typeof userInfo.idCH}) - Match: ${match}`
            );
            return match && canUserSeeReceipt(phieu);
          });
          console.log(
            `Filtered from ${originalLength} to ${data.length} records`
          );
        }
      }

      const sortedData = data.sort((a, b) => {
        if (typeof a.soPhieu === "string" && typeof b.soPhieu === "string") {
          return a.soPhieu.localeCompare(b.soPhieu);
        }
        return a.soPhieu - b.soPhieu;
      });

      setPhieuNhapList(sortedData);
    } catch (error) {
      console.error("Error fetching phiếu nhập:", error);
      setSnackbarMessage("Có lỗi khi tải dữ liệu phiếu nhập");
      setSnackbarOpen(true);
    }
  };

  const fetchPhieuNhapAll = async (fromDateParam = null, toDateParam = null) => {
    try {
      let apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/sale-nhap-hang-admin-all`;
      let params = {};
      // The API's MaCH filter only matches the receiving store. Fetch the full
      // list so the client-side filter can also include transfers sent by MaCH_DC.
      
      // Add date range parameters if provided
      if (fromDateParam && toDateParam) {
        params.fromDate = format(fromDateParam, "yyyy-MM-dd");
        params.toDate = format(toDateParam, "yyyy-MM-dd");
      }
      
      const response = await axios.get(apiUrl, { params });
      let data = response.data.map((phieu) => {
        // Check if there`s a discrepancy between ordered and original quantities
        let trangThai;

        // Kiểm tra nếu có bất kỳ item nào có SLThucNhan (không null/undefined)
        const hasSLThucNhan = phieu.Items.some(
          (item) => item.SLThucNhan !== null && item.SLThucNhan !== undefined
        );

        // Nếu có SLThucNhan thì mới check chênh lệch
        const hasDiscrepancy =
          hasSLThucNhan &&
          phieu.Items.some((item) => item.SLNhapDau !== item.SLThucNhan);

        trangThai = deriveTrangThai(phieu, {
          hasDiscrepancy,
          hasSLThucNhan,
        });

        // Calculate total quantity
        let tongSoLuong;
        if (isTruthyFlag(phieu.Act_phieu)) {
          // Use SLThucNhan for approved receipts, fallback to SLNhapDau
          tongSoLuong = phieu.Items.reduce(
            (sum, item) =>
              sum + (Number(item.SLThucNhan || item.SLNhapDau) || 0),
            0
          );
        } else {
          // Use SLNhapDau for non-approved receipts
          tongSoLuong = phieu.Items.reduce(
            (sum, item) => sum + (Number(item.SLNhapDau) || 0),
            0
          );
        }

        return {
          id: phieu.Phieu,
          soPhieu: phieu.SoPN,
          ngay: new Date(phieu.NgayNhap),
          createdAt: getCreatedAt(phieu),
          adminConfirmedAt: getAdminConfirmedAt(phieu),
          khachHang: phieu.MaCH,
          tenKH: phieu.TenKH || "",
          nguoiGui: phieu.MaCH_DC, // <-- add sender store id
          trangThai: trangThai,
          tongSoLuong: tongSoLuong,
          items: phieu.Items,
          createdBy: phieu.CreatedBy,
          isApproved: isTruthyFlag(phieu.Act_phieu),
          nguoiXacNhan: phieu.NguoiXacNhan || "",
          ghiChu: phieu.GhiChu,
          status: phieu.Status,
          loaiPhieu: phieu.LoaiPhieu || null, // Add LoaiPhieu field
        };
      });

      // Filter for records containing "Nhập bánh trung thu" in GhiChu or Status = 77
      data = data.filter(
        (phieu) =>
          (phieu.ghiChu && phieu.ghiChu.includes("Nhập bánh trung thu")) ||
          isTransferReceipt(phieu) ||
          isReturnReceipt(phieu)
      );

      // Special handling for IDCH 555 - show transfer/return receipts.
      if (userInfo.idCH === "555" || userInfo.idCH === 555) {
        data = data.filter(
          (phieu) =>
            (isTransferReceipt(phieu) || isReturnReceipt(phieu)) &&
            canUserSeeReceipt(phieu)
        );
      } else {
        // Filter data for user role based on IDCH from localStorage
        if (userRole === "user" && userInfo.idCH) {
          data = data.filter(
            (phieu) =>
              isReceiptVisibleForStore(phieu, userInfo.idCH) &&
              canUserSeeReceipt(phieu)
          );
        }
      }

      const sortedData = data.sort((a, b) => b.ngay - a.ngay);

      setPhieuNhapList(sortedData);
    } catch (error) {
      console.error("Error fetching phiếu nhập (all):", error);
      setSnackbarMessage("Có lỗi khi tải dữ liệu phiếu nhập (tất cả)");
      setSnackbarOpen(true);
    }
  };

  const handleShowAll = () => {
    setShowAll(true);
    setShowExportAllButton(true);
    // Set default date range (last 30 days)
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    setFromDate(thirtyDaysAgo);
    setToDate(today);
    fetchPhieuNhapAll(thirtyDaysAgo, today);
  };

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    setShowAll(false); // Khi chọn lại ngày thì quay về chế độ xem theo ngày
    setShowExportAllButton(false); // Hide export all button when date is changed
  };

  // Add new function to handle date range filtering
  const handleDateRangeFilter = () => {
    if (fromDate && toDate) {
      fetchPhieuNhapAll(fromDate, toDate);
    }
  };

  const handleReceiptContextMenu = (event, phieu) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY + 2,
      phieu,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleOpenReceiptInNewTab = () => {
    const id = contextMenu?.phieu?.id;
    if (id === null || id === undefined) return;

    window.open(`/phieunhaptt/${id}`, "_blank", "noopener,noreferrer");
    handleCloseContextMenu();
  };

  const handlePrintReceiptFromContextMenu = () => {
    const id = contextMenu?.phieu?.id;
    if (id === null || id === undefined) return;

    handleCloseContextMenu();
    handlePrintPhieu(id);
  };

  const handleViewPhieu = async (id) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/sale-nhap-hang-detail/${id}`
      );
      const selectedPhieu = response.data;
      navigate(`/phieunhaptt/${id}`, { state: { phieu: selectedPhieu } });
    } catch (error) {
      console.error("Error fetching phiếu nhập details:", error);
      setSnackbarMessage("Có lỗi khi tải chi tiết phiếu nhập");
      setSnackbarOpen(true);
    }
  };

  const handleAddPhieu = () => {
    if (userRole !== "coordinator") {
      setSnackbarMessage("Bạn không có quyền thêm phiếu nhập");
      setSnackbarOpen(true);
      return;
    }
    navigate("/newphieunhaptt");
  };

  const handleEditPhieu = async (id) => {
    if (userRole !== "coordinator") {
      setSnackbarMessage("Bạn không có quyền chỉnh sửa phiếu nhập");
      setSnackbarOpen(true);
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/sale-nhap-hang-detail/${id}`
      );
      const selectedPhieu = response.data;
      navigate(`/newphieunhaptt/${id}/edit`, {
        state: { phieu: selectedPhieu },
      });
    } catch (error) {
      console.error("Error fetching phiếu nhập details for editing:", error);
      setSnackbarMessage("Có lỗi khi tải chi tiết phiếu nhập để chỉnh sửa");
      setSnackbarOpen(true);
    }
  };

  const handlePrintPhieu = (id) => {
    if (userRole !== "admin") {
      setSnackbarMessage("Bạn không có quyền in phiếu nhập");
      setSnackbarOpen(true);
      return;
    }
    window.open(`/phieunhaptt/${id}`, "_blank");
  };

  const handleDelete = (id) => {
    const phieu = phieuNhapList.find((p) => p.id === id);

    if (userRole === "user") {
      setSnackbarMessage("Bạn không có quyền xóa phiếu nhập");
      setSnackbarOpen(true);
      return;
    }

    if (userRole === "coordinator" && phieu?.isApproved) {
      setSnackbarMessage("Phiếu đã được duyệt, cần admin phê duyệt để xóa");
      setSnackbarOpen(true);
      return;
    }

    if (userRole !== "admin" && userRole !== "coordinator") {
      setSnackbarMessage("Bạn không có quyền xóa phiếu nhập");
      setSnackbarOpen(true);
      return;
    }

    setDeleteTarget(phieu || { id });
  };

  const handleCloseDeleteModal = () => {
    if (deletingReceipt) return;
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    const id = deleteTarget?.id;
    if (id === null || id === undefined || deletingReceipt) return;

    setDeletingReceipt(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/sale-nhap-hang/${id}`, {
        data: {
          LoaiBanh: TRUNG_THU_LOAI_BANH,
          LoaiPhieu: TRUNG_THU_LOAI_PHIEU,
        },
      });

      const updatedPhieuNhapList = phieuNhapList.filter(
        (phieu) => phieu.id !== id
      );
      setPhieuNhapList(updatedPhieuNhapList);

      setSnackbarMessage("Đã xóa phiếu nhập thành công");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setDeleteTarget(null);
    } catch (error) {
      console.error("Error deleting phiếu nhập:", error);
      setSnackbarMessage("Có lỗi khi xóa phiếu nhập");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setDeletingReceipt(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const formatDate = (date) => {
    return format(date, "dd/MM/yyyy", { locale: vi });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Chờ admin xác nhận":
        return "#9C27B0";
      case "Chờ cửa hàng nhận":
        return "#FF9800";
      case "Hoàn tất":
        return "#4CAF50";
      case "Tự động nhận":
        return "#4CAF50";
      case "Đã duyệt":
        return "#4CAF50";
      case "Đã nhận, có chênh lệch":
        return "#FF9800";
      case "Cửa hàng đã xác nhận":
        return "#2196F3";
      case "Đã gửi phiếu":
        return "#FFC107";
      case "Đang giao":
        return "#2196F3";
      case "Đã hủy":
        return "#F44336";
      default:
        return "#757575";
    }
  };

  const handleExportExcel = async () => {
    try {
      // Filter only receipts that are complete enough for accounting export.
      const exportableReceipts = phieuNhapList.filter(isExportableReceipt);

      if (exportableReceipts.length === 0) {
        setSnackbarMessage(
          "Không có phiếu nào đủ điều kiện xuất Excel"
        );
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        return;
      }

      // Prepare data for Sheet 1: vB30AccDoc_EditSales
      const sheet1Data = [];
      // Prepare data for Sheet 2: vB30AccDocSales_Edit
      const sheet2Data = [];
      // Prepare data for Sheet 3: B30AccDocInfo
      const sheet3Data = [];

      exportableReceipts.forEach((phieu, index) => {
        if (!phieu.items || !Array.isArray(phieu.items)) {
          console.warn(`Receipt ${phieu.soPhieu} has no valid items array`);
          return;
        }

        const storeName = getStoreName(phieu.khachHang);
        const maBravo = getMaBravo(phieu.khachHang);
        const docCode = `HD`;
        const docDate = format(new Date(phieu.ngay), "dd/MM/yy");
        const docNo = index + 10;

        // Sheet 1 data - one row per receipt
        sheet1Data.push({
          BranchCode: "A01",
          DocCode: docCode,
          DocDate: docDate,
          DocNo: docNo,
          CustomerCode: maBravo,
          DocPro: 1,
          Person: storeName,
          Description: phieu.ghiChu || "",
          CurrencyCode: "VND",
          ExchangeRate: 1,
          TransCode: "B131",
        });

        // Sheet 2 data - one row per item
        phieu.items.forEach((item) => {
          const quantity = item.SLThucNhan || item.SLNhapDau || 0;
          sheet2Data.push({
            BranchCode: "A01",
            DocCode: docCode,
            DocDate: docDate,
            DocNo: docNo,
            CustomerCode: maBravo,
            TransCode: "B131",
            DebitAccount: "63226",
            CreditAccount: "1556",
            DebitAccount2: "1311",
            CreditAccount2: "51126",
            WarehouseCode: "X_TPB",
            ItemCode: item.MaSP || "",
            Unit: "cái",
            Quantity: quantity,
            ConvertRate9: 1,
            Quantity9: quantity,
          });
        });

        // Sheet 3 data - one row per receipt
        sheet3Data.push({
          BranchCode: "A01",
          DocCode: docCode,
          DocDate: docDate,
          DocNo: docNo,
          CustomerCode: maBravo,
          Address: storeName,
        });
      });

      if (
        sheet1Data.length === 0 &&
        sheet2Data.length === 0 &&
        sheet3Data.length === 0
      ) {
        setSnackbarMessage("Không có dữ liệu để xuất Excel");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        return;
      }

      // Create workbook
      const workbook = XLSX.utils.book_new();

      // Create Sheet 1: vB30AccDoc_EditSales
      const worksheet1 = XLSX.utils.json_to_sheet(sheet1Data);
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet1,
        "vB30AccDoc_EditSales"
      );

      // Create Sheet 2: vB30AccDocSales_Edit
      const worksheet2 = XLSX.utils.json_to_sheet(sheet2Data);
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet2,
        "vB30AccDocSales_Edit"
      );

      // Create Sheet 3: B30AccDocInfo
      const worksheet3 = XLSX.utils.json_to_sheet(sheet3Data);
      XLSX.utils.book_append_sheet(workbook, worksheet3, "B30AccDocInfo");

      // Generate filename with current date
      const currentDate = format(new Date(), "dd-MM-yyyy");
      const filename = `PhieuNhap_BanhTrungThu_${format(
        selectedDate,
        "dd-MM-yyyy"
      )}.xlsx`;

      // Save file
      XLSX.writeFile(workbook, filename);

      setSnackbarMessage(`Xuất file Excel thành công !!`);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error exporting Excel:", error);
      setSnackbarMessage("Có lỗi khi xuất file Excel");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleExportExcelAll = async () => {
    try {
      // Use filtered data instead of sortedPhieuNhapList
      const dataToExport = sortedPhieuNhapList;
      
      if (dataToExport.length === 0) {
        setSnackbarMessage("Không có dữ liệu để xuất Excel");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        return;
      }

      // Prepare data for Sheet 1: Phiếu
      const sheet1Data = dataToExport.map((phieu) => ({
        "Số Phiếu": phieu.soPhieu,
        "Người Gửi": getStoreName(phieu.nguoiGui),
        "Người nhận":
            isTransferReceipt(phieu) || isReturnReceipt(phieu)
            ? `${getStoreName(phieu.khachHang)} (${isTransferReceipt(phieu) ? "Điều chuyển" : "Trả về"})`
            : getStoreName(phieu.khachHang),
        "Ngày gửi": formatDate(phieu.ngay)
      }));

      // Prepare data for Sheet 2: Phiếu chi tiết phiếu
      const sheet2Data = [];
      dataToExport.forEach((phieu) => {
        if (phieu.items && Array.isArray(phieu.items)) {
          phieu.items.forEach((item) => {
            sheet2Data.push({
              "Số Phiếu": phieu.soPhieu,
              "MaSP": item.MaSP || "",
              "LoSX": item.LoSX || "",
              "Hạn sử dụng": item.HanSuDung ? formatDate(new Date(item.HanSuDung)) : "",
              "Số luợng": item.SLNhapDau || "null"
             });
          });
        }
      });

      // Prepare data for Sheet 3: Tổng hợp
      const sheet3Data = [];
      dataToExport.forEach((phieu) => {
        if (phieu.items && Array.isArray(phieu.items)) {
          phieu.items.forEach((item) => {
            sheet3Data.push({
              "Số phiếu": phieu.soPhieu,
              "Người gửi": getStoreName(phieu.nguoiGui),
              "Người nhận":
                isTransferReceipt(phieu) || isReturnReceipt(phieu)
                ? `${getStoreName(phieu.khachHang)} (${isTransferReceipt(phieu) ? "Điều chuyển" : "Trả về"})`
                : getStoreName(phieu.khachHang),
              "Ngày gửi": formatDate(phieu.ngay),
              "Mã sản phẩm": item.MaSP || "",
              "Lô SX": item.LoSX || "",
              "Hạn sử dụng": item.HanSuDung ? formatDate(new Date(item.HanSuDung)) : "",
              "Số lượng": item.SLNhapDau || 0,
            });
          });
        }
      });

      // Create workbook
      const workbook = XLSX.utils.book_new();

      // Create Sheet 1: Phiếu
      const worksheet1 = XLSX.utils.json_to_sheet(sheet1Data);
      XLSX.utils.book_append_sheet(workbook, worksheet1, "Phiếu");

      // Create Sheet 2: Phiếu chi tiết phiếu
      const worksheet2 = XLSX.utils.json_to_sheet(sheet2Data);
      XLSX.utils.book_append_sheet(workbook, worksheet2, "Phiếu chi tiết phiếu");

      // Create Sheet 3: Tổng hợp
      const worksheet3 = XLSX.utils.json_to_sheet(sheet3Data);
      XLSX.utils.book_append_sheet(workbook, worksheet3, "Tổng hợp");

      // Generate filename with date range
      const fromDateStr = format(fromDate, "dd-MM-yyyy");
      const toDateStr = format(toDate, "dd-MM-yyyy");
      const filename = `TatCaPhieu_BanhTrungThu_${fromDateStr}_${toDateStr}.xlsx`;

      // Save file
      XLSX.writeFile(workbook, filename);

      setSnackbarMessage(`Xuất file Excel thành công từ ${fromDateStr} đến ${toDateStr}!`);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error exporting Excel (all):", error);
      setSnackbarMessage("Có lỗi khi xuất file Excel tất cả");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Define the status order for sorting
  const statusOrder = [
    "Chờ admin xác nhận",
    "Chờ cửa hàng nhận",
    "Đã gửi phiếu",
    "Đã nhận, có chênh lệch",
    "Cửa hàng đã xác nhận",
    "Đã duyệt",
    "Tự động nhận",
    "Hoàn tất",
  ];

  const receiptTabs = [
    { id: "all", label: "Tất cả" },
    { id: "coordination", label: "Điều Phối" },
    { id: "transfer", label: "Điều Chuyển" },
    { id: "return", label: "Trả về" },
  ];

  const matchesReceiptTab = (phieu) => {
    if (activeReceiptTab === "transfer") return isTransferReceipt(phieu);
    if (activeReceiptTab === "return") return isReturnReceipt(phieu);
    if (activeReceiptTab === "coordination") {
      return !isTransferReceipt(phieu) && !isReturnReceipt(phieu);
    }

    return true;
  };

  // Lọc danh sách phiếu nhập theo MaCH hoặc TenCuaHang
  const filteredPhieuNhapList = phieuNhapList.filter((phieu) => {
    if (!matchesReceiptTab(phieu)) return false;

    // Filter by date range when showAll is true
    if (showAll && fromDate && toDate) {
      const phieuDate = new Date(phieu.ngay);
      const fromDateOnly = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
      const toDateOnly = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
      const phieuDateOnly = new Date(phieuDate.getFullYear(), phieuDate.getMonth(), phieuDate.getDate());
      
      if (phieuDateOnly < fromDateOnly || phieuDateOnly > toDateOnly) {
        return false;
      }
    }

    // Filter by search text
    if (!searchText) return true;
    const search = String(searchText ?? "").toLowerCase();
    const maCH = String(phieu.khachHang || "").toLowerCase();
    const tenCH = String(getStoreNameByMaCH(phieu.khachHang) || "").toLowerCase();
    return maCH.includes(search) || tenCH.includes(search);
  });

  // Create sorted list based on selected sort mode
  const sortedPhieuNhapList = sortWaitingTime
    ? [...filteredPhieuNhapList].sort(
        (a, b) => getWaitingMinutes(b) - getWaitingMinutes(a)
      )
    : sortStatus
    ? [...filteredPhieuNhapList].sort((a, b) => {
        const indexA = statusOrder.indexOf(getDisplayTrangThai(a));
        const indexB = statusOrder.indexOf(getDisplayTrangThai(b));
        return indexA - indexB;
      })
    : filteredPhieuNhapList;
  const showApprovedByColumn = true;

  return (
    <ThemeProvider theme={theme}>
      <Navbar />
      <Container
        maxWidth="xl"
        sx={{
          mt: { xs: 2, md: 4 },
          mb: { xs: 2, md: 4 },
          px: { xs: 1, sm: 2, md: 3 },
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              mb: 3,
              gap: { xs: 2, sm: 1 },
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: "bold",
                color: "#d32f2f",
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" },
              }}
            >
              Danh Sách điều phối Bánh Trung Thu
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Button
                variant="outlined"
                color="success"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportExcel}
                sx={{
                  borderRadius: 8,
                  boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                  fontSize: { xs: "0.875rem", md: "1rem" },
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 12px rgba(0,0,0,0.3)",
                  },
                }}
              >
                Xuất Excel
              </Button>

              {showExportAllButton && (
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportExcelAll}
                  sx={{
                    borderRadius: 8,
                    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                    fontSize: { xs: "0.875rem", md: "1rem" },
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 12px rgba(0,0,0,0.3)",
                    },
                  }}
                >
                  Xuất Excel Tất Cả
                </Button>
              )}

              {userRole === "coordinator" && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddPhieu}
                  sx={{
                    borderRadius: 8,
                    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                    fontSize: { xs: "0.875rem", md: "1rem" },
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 12px rgba(0,0,0,0.3)",
                    },
                  }}
                >
                  Thêm Phiếu Nhập
                </Button>
               )}
            </Box>
          </Box>

          {/* Thanh tìm kiếm MaCH hoặc TenCuaHang + tab loại phiếu */}
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Tìm theo mã cửa hàng hoặc tên cửa hàng..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{ width: { xs: "100%", sm: 350 } }}
            />
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {receiptTabs.map((tab) => (
                <Button
                  key={tab.id}
                  variant={activeReceiptTab === tab.id ? "contained" : "outlined"}
                  color={activeReceiptTab === tab.id ? "primary" : "inherit"}
                  size="small"
                  onClick={() => setActiveReceiptTab(tab.id)}
                  sx={{
                    borderRadius: 8,
                    textTransform: "none",
                    fontWeight: 600,
                    minWidth: { xs: 88, sm: "auto" },
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              mb: 3,
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                mr: { sm: 2 },
                fontSize: { xs: "0.875rem", md: "1rem" },
              }}
            >
              Chọn ngày:
            </Typography>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={vi}
            >
              <DatePicker
                value={selectedDate}
                onChange={handleDateChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    sx={{ width: { xs: "100%", sm: 200 } }}
                  />
                )}
                disabled={showAll}
              />
            </LocalizationProvider>
            <Button
              variant={showAll ? "contained" : "outlined"}
              color="secondary"
              sx={{
                ml: { sm: 2 },
                mt: { xs: 1, sm: 0 },
                borderRadius: 8,
                fontWeight: 600,
                fontSize: { xs: "0.85rem", md: "1rem" },
                minWidth: 120,
                boxShadow: showAll ? "0 4px 8px rgba(0,0,0,0.15)" : "none",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 12px rgba(0,0,0,0.18)",
                },
              }}
              onClick={handleShowAll}
              disabled={showAll}
            >
              Xem tất cả
            </Button>
          </Box>

          {/* Date range picker - only show when showAll is true */}
          {showAll && (
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                mb: 3,
                gap: { xs: 1, sm: 2 },
                p: 2,
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  mr: { sm: 2 },
                  fontSize: { xs: "0.875rem", md: "1rem" },
                  fontWeight: "bold",
                }}
              >
                Lọc theo khoảng thời gian:
              </Typography>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={vi}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography variant="body2">Từ ngày:</Typography>
                  <DatePicker
                    value={fromDate}
                    onChange={(newDate) => setFromDate(newDate)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        sx={{ width: { xs: "100%", sm: 150 } }}
                      />
                    )}
                  />
                  <Typography variant="body2">Đến ngày:</Typography>
                  <DatePicker
                    value={toDate}
                    onChange={(newDate) => setToDate(newDate)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        sx={{ width: { xs: "100%", sm: 150 } }}
                      />
                    )}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleDateRangeFilter}
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    Lọc
                  </Button>
                </Box>
              </LocalizationProvider>
            </Box>
          )}

          <TableContainer
            component={Paper}
            elevation={2}
            sx={{
              mb: 3,
              overflowX: "auto",
              "& .MuiTable-root": {
                minWidth: { xs: showApprovedByColumn ? 560 : 460, md: "auto" },
              },
              "& .MuiTableCell-root": {
                px: { xs: 1, sm: 2 },
                py: { xs: 1.25, sm: 1.5 },
                verticalAlign: "middle",
              },
            }}
          >
            <Table>
              <TableHead sx={{ backgroundColor: "#ffcccb" }}>
                <TableRow>
                  <TableCell
                    sx={{
                      display: { xs: "none", sm: "table-cell" },
                      fontWeight: "bold",
                      fontSize: { xs: "0.875rem", md: "0.875rem" },
                      minWidth: { xs: 80, md: "auto" },
                    }}
                  >
                    Số Phiếu
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", sm: "table-cell" },
                      fontWeight: "bold",
                      fontSize: { xs: "0.875rem", md: "0.875rem" },
                      minWidth: { xs: 90, md: "auto" },
                    }}
                  >
                    Ngày
                  </TableCell>
                  {/* NEW COLUMN: Người gửi (before Khách Hàng) */}
                  <TableCell
                    sx={{
                      display: "table-cell",
                      fontWeight: "bold",
                      fontSize: { xs: "0.875rem", md: "0.875rem" },
                      minWidth: { xs: 100, md: "auto" },
                    }}
                  >
                    Người gửi
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", sm: "table-cell" },
                      fontWeight: "bold",
                      fontSize: { xs: "0.875rem", md: "0.875rem" },
                      minWidth: { xs: 100, md: "auto" },
                    }}
                  >
                    Khách Hàng (Người nhận)
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.875rem", md: "0.875rem" },
                      minWidth: { xs: 90, md: "auto" },
                    }}
                  >
                    Trạng Thái
                    <IconButton
                      onClick={() => {
                        setSortStatus(!sortStatus);
                        setSortWaitingTime(false);
                      }}
                      size="small"
                      sx={{ ml: 1 }}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                  {showApprovedByColumn && (
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        fontSize: { xs: "0.875rem", md: "0.875rem" },
                        minWidth: { xs: 100, md: "auto" },
                      }}
                    >
                      Duyệt bởi
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.875rem", md: "0.875rem" },
                      minWidth: { xs: 110, md: "auto" },
                    }}
                  >
                    Thời gian chờ
                    <Tooltip title="Sắp xếp thời gian chờ lâu nhất trước">
                      <IconButton
                        onClick={() => {
                          setSortWaitingTime(!sortWaitingTime);
                          setSortStatus(false);
                        }}
                        size="small"
                        sx={{ ml: 1 }}
                      >
                        <ArrowUpwardIcon
                          fontSize="small"
                          sx={{
                            transform: sortWaitingTime
                              ? "rotate(180deg)"
                              : "none",
                            transition: "transform 0.2s ease",
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.875rem", md: "0.875rem" },
                      minWidth: { xs: 80, md: "auto" },
                    }}
                  >
                    Tổng SL
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      display: { xs: "none", sm: "table-cell" },
                      fontWeight: "bold",
                      fontSize: { xs: "0.875rem", md: "0.875rem" },
                      minWidth: { xs: 140, md: "auto" },
                    }}
                  >
                    Thao Tác
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedPhieuNhapList.length > 0 ? (
                  sortedPhieuNhapList.map((phieu) => (
                    <TableRow
                      key={phieu.id}
                      hover
                      onClick={() => handleViewPhieu(phieu.id)}
                      onContextMenu={(event) => handleReceiptContextMenu(event, phieu)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell
                        sx={{
                          display: { xs: "none", sm: "table-cell" },
                          fontSize: { xs: "0.875rem", md: "0.875rem" },
                        }}
                      >
                        {phieu.soPhieu}
                      </TableCell>
                      <TableCell
                        sx={{
                          display: { xs: "none", sm: "table-cell" },
                          fontSize: { xs: "0.875rem", md: "0.875rem" },
                        }}
                      >
                        {formatDate(phieu.ngay)}
                      </TableCell>
                      {/* NEW CELL: Người gửi */}
                      <TableCell
                        sx={{
                          display: "table-cell",
                          fontSize: { xs: "0.875rem", md: "0.875rem" },
                        }}
                      >
                        {getStoreName(phieu.nguoiGui)}
                      </TableCell>
                      <TableCell
                        sx={{
                          display: { xs: "none", sm: "table-cell" },
                          fontSize: { xs: "0.75rem", md: "0.875rem" },
                        }}
                      >
                        {getReceiverDisplayName(phieu)}
                      </TableCell>
                      <TableCell>
                        <Box
                          component="span"
                          sx={{
                            color: "white",
                            bgcolor: getStatusColor(getDisplayTrangThai(phieu)),
                            px: { xs: 1, md: 1.5 },
                            py: 0.5,
                            borderRadius: 1,
                            display: "inline-block",
                            fontSize: { xs: "0.8125rem", md: "0.875rem" },
                            lineHeight: 1.2,
                          }}
                        >
                          {getDisplayTrangThai(phieu)}
                        </Box>
                      </TableCell>
                      {showApprovedByColumn && (
                        <TableCell
                          sx={{ fontSize: { xs: "0.875rem", md: "0.875rem" } }}
                        >
                          {renderApprovedByBadge(phieu.nguoiXacNhan)}
                        </TableCell>
                      )}
                      <TableCell
                        sx={{ fontSize: { xs: "0.875rem", md: "0.875rem" } }}
                      >
                        {renderWaitingTimeBadge(phieu)}
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: { xs: "0.875rem", md: "0.875rem" } }}
                      >
                        {phieu.tongSoLuong}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ display: { xs: "none", sm: "table-cell" } }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            gap: { xs: 0.5, sm: 0 },
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <Tooltip title="Xem phiếu">
                            <IconButton
                              color="primary"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleViewPhieu(phieu.id);
                              }}
                              size="small"
                              sx={{ minWidth: { xs: 32, md: "auto" } }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {userRole === "admin" && (
                            <Tooltip title="In phiếu">
                              <IconButton
                                color="secondary"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handlePrintPhieu(phieu.id);
                                }}
                                size="small"
                                sx={{ minWidth: { xs: 32, md: "auto" } }}
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {userRole === "coordinator" && (
                            <Tooltip title="Chỉnh sửa">
                              <IconButton
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleEditPhieu(phieu.id);
                                }}
                                size="small"
                                sx={{ minWidth: { xs: 32, md: "auto" } }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {(userRole === "admin" ||
                            userRole === "coordinator") && (
                            <Tooltip title="Xóa">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleDelete(phieu.id);
                                }}
                                sx={{ minWidth: { xs: 32, md: "auto" } }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    {/* update colSpan due to new column */}
                    <TableCell
                      colSpan={showApprovedByColumn ? 9 : 8}
                      align="center"
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          py: 2,
                          fontSize: { xs: "0.875rem", md: "1rem" },
                        }}
                      >
                        Không tìm thấy phiếu nhập hàng nào cho ngày{" "}
                        {formatDate(selectedDate)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Menu
            open={Boolean(contextMenu)}
            onClose={handleCloseContextMenu}
            anchorReference="anchorPosition"
            ModalProps={{ hideBackdrop: true }}
            transitionDuration={0}
            anchorPosition={
              contextMenu
                ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                : undefined
            }
          >
            <MenuItem onClick={handleOpenReceiptInNewTab}>
              Mở trong tab mới
            </MenuItem>
            <MenuItem onClick={handlePrintReceiptFromContextMenu}>
              In
            </MenuItem>
          </Menu>

          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
              mt: 3,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontStyle: "italic",
                color: "#666",
                fontSize: { xs: "0.75rem", md: "0.875rem" },
              }}
            >
              Tổng số phiếu: {phieuNhapList.length}
            </Typography>
          </Box>
        </Paper>

        <Dialog
          open={Boolean(deleteTarget)}
          onClose={handleCloseDeleteModal}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              borderTop: "4px solid #d32f2f",
            },
          }}
        >
          <DialogTitle sx={{ color: "error.main", fontWeight: 700 }}>
            Xác nhận xóa phiếu nhập
          </DialogTitle>
          <DialogContent>
            <Typography>
              Bạn có muốn xóa phiếu nhập này?
            </Typography>
            {(deleteTarget?.soPhieu || deleteTarget?.id) && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                Số phiếu: <strong>{deleteTarget.soPhieu || deleteTarget.id}</strong>
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={handleCloseDeleteModal}
              color="inherit"
              disabled={deletingReceipt}
            >
              Hủy
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={deletingReceipt}
              startIcon={
                deletingReceipt ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DeleteIcon />
                )
              }
            >
              {deletingReceipt ? "Đang xóa..." : "Xóa phiếu"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </ThemeProvider>
  );
};

export default NhapHangTT;
